import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AdvisoryFilters, type AdvisoryFiltersState } from '@/components/screens/advisory/AdvisoryFilters';
import { AdvisoryFormModal, type EditingAdvisory, type AdvisoryType } from '@/components/screens/advisory/AdvisoryFormModal';
import { AdvisoryAckNamesModal } from '@/components/screens/advisory/AdvisoryAckNamesModal';
import { validateAdvisory, hasErrors, browserZoneId, formatHhmmForDisplay, hhmmToMinutes, type AdvisoryFieldErrors } from '@/components/screens/advisory/advisoryValidation';
import { computeRecurrenceState, formatRecurrenceDays, zoneShortName } from '@/components/screens/advisory/advisorySchedule';

import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { uploadBlockBlob } from '@/services/azure-blob-service';
import { load } from '@/services/loading';
import moment from 'moment';
import { currentUserHasPermission } from '@/utils/legacy-utils';
import { getCurrentCompanyId, getCurrentUserId } from '@/utils/auth';
import { AccessAuthorities } from '@/models';
import { Codes } from '@/constants/codes';

// ============================================================================
// AdvisoryScreen — single adaptive OPERATIONAL surface (CAD + MDT)
// ----------------------------------------------------------------------------
// Merges the old read-only Officer Advisory Board (color-coded cards) with the
// old management screen's Advisories CRUD into one permission-scoped screen:
//   - ROLE_ORNA_ADVISORY_READ   → cards only (filter bar available to all)
//   - ROLE_ORNA_ADVISORY_SAVE   → "New Advisory" button + add form modal
//   - ROLE_ORNA_ADVISORY_UPDATE → edit / restore affordances per card
//   - ROLE_ORNA_ADVISORY_DELETE → delete affordance per card
//
// No left nav, no tabs. Data via api.advisorySearch (status/client/type/text
// filters + pagination). Live via realtime push (advisoryChanged) + on-focus
// reload, with a ~5-min safety poll for time-derived status transitions.
// ============================================================================

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const deepCopyProperties = _wg('deepCopyProperties');
const getAttachmentInfoList = _wg('getAttachmentInfoList');
const getObjectInArrayByProperty = _wg('getObjectInArrayByProperty');

// Adaptive acknowledgment timestamp: same calendar day shows a bare time, an
// older ack keeps its date so a day-old ack is never a context-free time. Shared
// by the acknowledged pill, the stale note, and the board "reviewed" label below.
function formatAckTimestamp(iso: string): string {
  const m = moment(iso);
  return m.isSame(moment(), 'day') ? m.format('h:mm A') : m.format('MMM D, h:mm A');
}

// ============================================================================
// Module-level page refresh interval (mirrors NotesScreen / old board)
// ============================================================================

let advisoryScreenRefreshInterval: ReturnType<typeof setInterval> | null = null;
let advisoryScreenRefreshCallback: (() => void) | null = null;

function clearAdvisoryScreenRefresh(): void {
  if (advisoryScreenRefreshInterval) {
    clearInterval(advisoryScreenRefreshInterval);
    advisoryScreenRefreshInterval = null;
  }
}

function resetAdvisoryScreenRefresh(): void {
  clearAdvisoryScreenRefresh();
  // ~5-min safety poll for time-derived status transitions (ACTIVE→EXPIRED,
  // SCHEDULED→ACTIVE). Realtime pushes ('orna:advisory-changed') drive the
  // immediate reloads on create/update/delete/restore.
  advisoryScreenRefreshInterval = setInterval(() => {
    if (advisoryScreenRefreshCallback) {
      advisoryScreenRefreshCallback();
    }
  }, 300000);
}

// ============================================================================
// Types
// ============================================================================

interface AdvisorySite {
  id: number;
  siteName: string;
}

interface AdvisoryCurrentUserAck {
  acknowledgedAt: string;
  stale: boolean;
}

interface AdvisoryListItem {
  id: number;
  displayId: number;
  advisoryTypeId: number;
  typeName: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  recipientType: string;
  npsdClient: number | null;
  npsdClientName: string;
  selectedClientSites: AdvisorySite[];
  subject: string;
  details: string;
  location: string;
  loggedBy: number;
  loggedByName: string;
  dateCreated: string | null;
  lastUpdated: string | null;
  startDateTime: string | null;
  endDateTime: string | null;
  deleted: boolean;
  status: 'ACTIVE' | 'SCHEDULED' | 'EXPIRED' | 'DELETED';
  // Urgency + recurrence (spec §4.2). All optional / defaulted for backward
  // compatibility with rows that predate these fields.
  urgent: boolean;
  recurring: boolean;
  recurrenceDays: string[];
  recurrenceStartTime: string | null;
  recurrenceEndTime: string | null;
  recurrenceZoneId: string | null;
  // Per-advisory acknowledgment (urgent rows only; null otherwise).
  currentUserAcknowledgment: AdvisoryCurrentUserAck | null;
  acknowledgedCount: number | null;
  acknowledgedTotal: number | null;
  attachmentList: any[];
  resizedImageList: any[];
}

// ============================================================================
// Helpers
// ============================================================================

const FALLBACK_BG = 'eeeeee';
const FALLBACK_BORDER = 'cccccc';
const FALLBACK_TEXT = '000000';

function createEmptyEditingAdvisory(): EditingAdvisory {
  return {
    id: null,
    displayId: null,
    advisoryTypeId: null,
    recipientType: '',
    npsdClient: null,
    selectedSiteIds: [],
    subject: '',
    details: '',
    location: '',
    urgent: false,
    isTemporary: false,
    recurring: false,
    recurrenceDays: [],
    recurrenceStartTime: '',
    recurrenceEndTime: '',
    startDateTime: null,
    endDateTime: null,
    attachmentList: [],
    resizedImageList: [],
    attachmentInfoList: [],
  };
}

// Type-chip colors come from the API (per advisory type) and cannot be classes,
// so they remain as a minimal inline color-only style.
function chipColors(row: AdvisoryListItem): React.CSSProperties {
  return {
    color: '#' + (row.textColor || FALLBACK_TEXT),
    backgroundColor: '#' + (row.backgroundColor || FALLBACK_BG),
    border: '1px solid #' + (row.borderColor || FALLBACK_BORDER),
  };
}

function statusLabel(status: AdvisoryListItem['status']): string {
  if (status === 'ACTIVE') return 'Active';
  if (status === 'SCHEDULED') return 'Scheduled';
  if (status === 'DELETED') return 'Deleted';
  return 'Expired';
}

function sitesLabel(row: AdvisoryListItem): string {
  if (row.selectedClientSites && row.selectedClientSites.length > 0) {
    // Collapse a long site list to "first + N more" (mirrors attachmentLabel) so
    // the meta line stays on one line; the full list is exposed via a title.
    const first = row.selectedClientSites[0];
    const name = (first && first.siteName) || 'site';
    if (row.selectedClientSites.length > 1) {
      return `${name} + ${row.selectedClientSites.length - 1} more`;
    }
    return name;
  }
  return 'All sites';
}

// Full comma-joined site list for a title tooltip, but only when the meta line's
// sitesLabel is actually truncated (more than one site on a client-scoped row).
function sitesTitle(row: AdvisoryListItem): string | undefined {
  if (
    row.recipientType === 'c' &&
    row.selectedClientSites &&
    row.selectedClientSites.length > 1
  ) {
    return row.selectedClientSites.map((s) => s.siteName).join(', ');
  }
  return undefined;
}

function metaLabel(row: AdvisoryListItem): string {
  // Distinguish by recipient type ('c'=client, 'i'=internal, 'a'=all clients),
  // not by whether a client is set — internal and all-clients both lack a client.
  if (row.recipientType === 'c') {
    const client = row.npsdClientName || 'Client';
    return `${client} · ${sitesLabel(row)}`;
  }
  if (row.recipientType === 'i') {
    return 'Internal';
  }
  return 'All clients';
}

function footLogged(row: AdvisoryListItem): string {
  const by = row.loggedByName || '—';
  // Use the creation date (not the window start, which is a future/scheduled
  // date for scheduled advisories and absent for permanent ones).
  if (row.dateCreated) {
    return `Logged by ${by} · ${moment(row.dateCreated).format('MMM D')}`;
  }
  return `Logged by ${by}`;
}

// Explicit start → end window for a time-bounded advisory. Only called when the row
// has a start or end date; a standing (no-window) advisory renders a muted "Ongoing"
// marker instead of a window label.
function windowLabel(row: AdvisoryListItem): string {
  const start = row.startDateTime ? moment(row.startDateTime).format('MMM D') : '—';
  const end = row.endDateTime ? moment(row.endDateTime).format('MMM D, YYYY h:mm A') : '—';
  return `${start} → ${end}`;
}

// A recurring window's next opening, in the VIEWER's local time — that's what an
// officer glancing at a tablet expects. "today"/"tomorrow" read more naturally
// than a weekday name when the window is imminent.
function formatNextWindow(next: Date, now: Date): string {
  const m = moment(next);
  const diffDays = m.clone().startOf('day').diff(moment(now).startOf('day'), 'days');
  const time = m.format('h:mm A');
  if (diffDays <= 0) return `today ${time}`;
  if (diffDays === 1) return `tomorrow ${time}`;
  return `${m.format('ddd')} ${time}`;
}

function attachmentLabel(row: AdvisoryListItem): string | null {
  if (!row.attachmentList || row.attachmentList.length === 0) return null;
  const first = row.attachmentList[0];
  const name = first && (first.filename || first.title);
  if (row.attachmentList.length > 1) {
    return `${name || 'attachment'} + ${row.attachmentList.length - 1} more`;
  }
  return `${name || 'attachment'}`;
}

// Freshness vs the officer's last board review (acknowledgedAt). NEW = created
// after the last review (or never reviewed). UPDATED = edited after the last
// review, but created before it (so it isn't brand new to this officer).
type Freshness = 'new' | 'updated' | null;
function freshnessFor(row: AdvisoryListItem, lastReviewedAt: string | null): Freshness {
  // Only ACTIVE/SCHEDULED items get freshness flags; expired/deleted don't.
  if (row.status === 'EXPIRED' || row.status === 'DELETED') return null;
  if (!lastReviewedAt) return 'new';
  const reviewed = new Date(lastReviewedAt).getTime();
  const created = row.dateCreated ? new Date(row.dateCreated).getTime() : 0;
  const updated = row.lastUpdated ? new Date(row.lastUpdated).getTime() : created;
  if (created > reviewed) return 'new';
  if (updated > reviewed) return 'updated';
  return null;
}

// Stable urgent-first ordering (backend already sorts urgent DESC; we re-assert
// it client-side so the pinned-first guarantee holds even for cached/offline data).
function sortUrgentFirst(rows: AdvisoryListItem[]): AdvisoryListItem[] {
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const au = a.r.urgent ? 1 : 0;
      const bu = b.r.urgent ? 1 : 0;
      if (au !== bu) return bu - au;
      return a.i - b.i;
    })
    .map((x) => x.r);
}

// Offline last-known board cache (localStorage, scoped per user + company so a
// shared device never shows another user's board). Value = { at, rows }.
function offlineCacheKey(): string {
  const companyId = getCurrentCompanyId() || 'c';
  const userId = getCurrentUserId() || 'u';
  return `orna:advisory-board-cache:${companyId}:${userId}`;
}

// ============================================================================
// AdvisoryScreen
// ============================================================================

export function AdvisoryScreen() {
  const { theme } = useTheme();

  // --- Permissions ---
  const canCreate = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_ORNA_ADVISORY_SAVE),
    []
  );
  const canModify = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_ORNA_ADVISORY_UPDATE),
    []
  );
  const canDelete = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_ORNA_ADVISORY_DELETE),
    []
  );
  // Everyone lands on Active so expired/deleted cards don't clutter the board at
  // shift start; the Status dropdown still lets editors pick All / Expired /
  // Deleted to find and manage older items.
  const canEditAny = canCreate || canModify || canDelete;
  const defaultStatus = 'ACTIVE';

  // --- List state ---
  const [isLoading, setIsLoading] = useState(false);
  const [advisories, setAdvisories] = useState<AdvisoryListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [filters, setFilters] = useState<AdvisoryFiltersState>({
    status: defaultStatus,
    client: null,
    npsdClientSiteId: null,
    advisoryTypeId: null,
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  // Sites for the FILTER's selected client (independent of the form modal's sites).
  const [filterClientSites, setFilterClientSites] = useState<Array<{ id: number; siteName: string }>>([]);

  // --- Board header counts (from the board response; spec §4.1) ---
  const [totalActive, setTotalActive] = useState<number | null>(null);
  const [totalUrgent, setTotalUrgent] = useState<number | null>(null);
  const [boardTruncated, setBoardTruncated] = useState(false);
  const [boardCap, setBoardCap] = useState<number | null>(null);

  // --- Nav/review badge count (unreviewed since last board review; spec §4.6) ---
  const [unreviewedCount, setUnreviewedCount] = useState(0);

  // --- Load error + offline last-known board ---
  const [loadError, setLoadError] = useState(false);
  const [offlineAt, setOfflineAt] = useState<string | null>(null);
  const [showOffline, setShowOffline] = useState(false);

  // --- Inline field validation for the form modal ---
  const [formErrors, setFormErrors] = useState<AdvisoryFieldErrors>({});

  // --- Per-advisory acknowledgment (urgent cards) ---
  const [ackingId, setAckingId] = useState<number | null>(null);
  const [ackNamesOpen, setAckNamesOpen] = useState(false);
  const [ackNamesLoading, setAckNamesLoading] = useState(false);
  const [ackNamesSubject, setAckNamesSubject] = useState('');
  const [ackNames, setAckNames] = useState<any[]>([]);
  const [ackNamesError, setAckNamesError] = useState(false);

  // --- Types ---
  const [advisoryTypes, setAdvisoryTypes] = useState<AdvisoryType[]>([]);

  // --- Dropdown data ---
  const [recipientTypes, setRecipientTypes] = useState<Array<{ code: string; value: string }>>([]);
  const [clients, setClients] = useState<Array<{ id: number; name: string }>>([]);
  const [clientSites, setClientSites] = useState<Array<{ id: number; siteName: string }>>([]);

  // --- Form modal state ---
  const [showFormModal, setShowFormModal] = useState(false);
  const [isNewAdvisory, setIsNewAdvisory] = useState(true);
  // Read-only "View" mode: any user (including read-only officers) opens a card to
  // see full details + downloadable attachments with all inputs disabled.
  const [isReadOnlyView, setIsReadOnlyView] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [editingAdvisory, setEditingAdvisory] = useState<EditingAdvisory>(createEmptyEditingAdvisory());
  const [isStartPickerOpen, setIsStartPickerOpen] = useState(false);
  const [isEndPickerOpen, setIsEndPickerOpen] = useState(false);

  // --- Delete modal state ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [advisoryToDelete, setAdvisoryToDelete] = useState<AdvisoryListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Per-row in-flight guard for Restore (mirrors ackingId) so the button can be
  // disabled while its PUT is pending and won't double-fire.
  const [restoringId, setRestoringId] = useState<number | null>(null);

  // --- Board-level acknowledgement ("Mark reviewed") ---
  const [lastReviewedAt, setLastReviewedAt] = useState<string | null>(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  // --- Live clock for recurring-card schedule state (1-min tick; see effect) ---
  const [nowTick, setNowTick] = useState(() => Date.now());
  // The viewer's zone, resolved once. Recurring schedule times are shown in the
  // advisory's zone on the schedule line (with a short zone label when it differs
  // from the viewer's), but the live pill times are shown in the viewer's zone.
  const viewerZone = useMemo(() => browserZoneId(), []);

  // --- Refs ---
  // logView is sent true ONLY on the very first mount load (board-view audit);
  // filter changes, the safety poll, realtime pushes and focus reloads send false.
  const firstLoadRef = useRef(true);
  // Board loads run without the global blocking overlay, so requests can overlap
  // (poll / push / focus reloads vs. a user filter/page change). Track the latest
  // request's identity so stale responses don't overwrite newer state.
  const loadRequestIdRef = useRef(0);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const editingAdvisoryRef = useRef(editingAdvisory);
  editingAdvisoryRef.current = editingAdvisory;

  // --- Computed ---
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status && filters.status !== defaultStatus) count++;
    if (filters.client) count++;
    if (filters.npsdClientSiteId) count++;
    if (filters.advisoryTypeId) count++;
    return count;
  }, [filters, defaultStatus]);

  const filterSiteItems = useMemo(
    () => filterClientSites.map((s) => ({ label: s.siteName, value: s.id as number | null })),
    [filterClientSites]
  );

  const clientItems = useMemo(
    () => clients.map((c) => ({ label: c.name, value: c.id })),
    [clients]
  );
  const recipientTypeItems = useMemo(
    () => recipientTypes.map((rt) => ({ label: rt.value, value: rt.code })),
    [recipientTypes]
  );
  const typeFilterItems = useMemo(
    () => advisoryTypes.map((t) => ({ label: t.type, value: t.id as number | null })),
    [advisoryTypes]
  );

  const deleteConfirmMessage = useMemo(() => {
    const id = advisoryToDelete ? advisoryToDelete.displayId : '';
    return `<p>Delete advisory <strong>#${id}</strong>?</p><p>It will be removed from the board, but you can restore it later.</p>`;
  }, [advisoryToDelete]);

  // ===== Load functions =====

  const loadAdvisories = useCallback((pageOverride?: number) => {
    setIsLoading(true);
    const requestId = ++loadRequestIdRef.current;
    const page = pageOverride !== undefined ? pageOverride : currentPageRef.current;
    const currentFilters = filtersRef.current;

    // Build a Kendo-style filter array matching the backend's accepted vocabulary
    // (AdvisoryService.buildAdvisorySpecification / AdvisoryFiltersCommand).
    const filterMaps: Array<{ field: string; operator: string; value: string }> = [];
    // Always send status (even 'ALL') — the backend defaults to ACTIVE when omitted.
    filterMaps.push({ field: 'status', operator: 'eq', value: currentFilters.status });
    if (currentFilters.client != null) {
      filterMaps.push({ field: 'npsdClientId', operator: 'eq', value: String(currentFilters.client) });
    }
    if (currentFilters.npsdClientSiteId != null) {
      filterMaps.push({ field: 'npsdClientSiteId', operator: 'eq', value: String(currentFilters.npsdClientSiteId) });
    }
    if (currentFilters.advisoryTypeId != null) {
      filterMaps.push({ field: 'advisoryTypeId', operator: 'eq', value: String(currentFilters.advisoryTypeId) });
    }

    // logView (sibling of data) is true ONLY for the initial mount load so the
    // backend records a single board-view audit entry; all subsequent reloads
    // (filters, poll, realtime push, focus) send false.
    const logView = firstLoadRef.current;
    firstLoadRef.current = false;

    const operation = {
      logView: logView,
      data: {
        page: page,
        pageSize: pageSize,
        filter: { logic: 'and', filters: filterMaps },
      },
    };

    api.advisorySearch(
      page,
      String(pageSize),
      JSON.stringify(operation),
      (result: any) => {
        if (requestId !== loadRequestIdRef.current) return; // stale — a newer load superseded this one
        const newTotalCount = result.records || 0;
        const newTotalPages = Math.ceil(newTotalCount / pageSize) || 1;
        const list = result.advisories || [];

        const mapped: AdvisoryListItem[] = sortUrgentFirst(list.map(mapAdvisoryRow));

        // A successful load clears any prior error/offline banner and refreshes
        // the last-known cache used for the offline fallback.
        setTotalPages(newTotalPages);
        setTotalRecords(newTotalCount);
        setAdvisories(mapped);
        setLoadError(false);
        setShowOffline(false);
        setIsLoading(false);
        try {
          localStorage.setItem(
            offlineCacheKey(),
            JSON.stringify({ at: new Date().toISOString(), rows: mapped })
          );
        } catch {
          /* storage full / disabled — offline fallback simply won't be available */
        }
      },
      () => {
        if (requestId !== loadRequestIdRef.current) return; // stale — a newer load superseded this one
        // Distinct error state (never render the empty state on failure). Load the
        // last-known cache so the operator can fall back to it.
        setIsLoading(false);
        setLoadError(true);
        let cachedAt: string | null = null;
        try {
          const raw = localStorage.getItem(offlineCacheKey());
          if (raw) {
            const parsed = JSON.parse(raw);
            cachedAt = parsed && parsed.at ? parsed.at : null;
          }
        } catch {
          cachedAt = null;
        }
        setOfflineAt(cachedAt);
      }
    );
  }, []);

  // Maps a raw advisory row (search or board) to the view model, reading the
  // urgency/recurrence/ack fields defensively so older payloads still render.
  const mapAdvisoryRow = useCallback((a: any): AdvisoryListItem => ({
    id: a.id,
    // Per-company display id (AdvisoryDisplayIdCounter); fall back to the raw
    // DB id only if the backend omits it.
    displayId: a.displayId != null ? a.displayId : a.id,
    advisoryTypeId: a.advisoryTypeId,
    typeName: a.typeName || '',
    backgroundColor: a.backgroundColor || '',
    borderColor: a.borderColor || '',
    textColor: a.textColor || '',
    recipientType: a.recipientType,
    npsdClient: a.npsdClient,
    npsdClientName: a.npsdClientName || '',
    selectedClientSites: a.selectedClientSites || [],
    subject: a.subject || '',
    details: a.details || '',
    location: a.location || '',
    loggedBy: a.loggedBy,
    loggedByName: a.loggedByName || '',
    dateCreated: a.dateCreated || null,
    lastUpdated: a.lastUpdated || null,
    startDateTime: a.startDateTime || null,
    endDateTime: a.endDateTime || null,
    deleted: !!a.deleted,
    status: a.status || 'ACTIVE',
    urgent: !!a.urgent,
    recurring: !!a.recurring,
    recurrenceDays: a.recurrenceDays || [],
    recurrenceStartTime: a.recurrenceStartTime || null,
    recurrenceEndTime: a.recurrenceEndTime || null,
    recurrenceZoneId: a.recurrenceZoneId || null,
    currentUserAcknowledgment: a.currentUserAcknowledgment || null,
    acknowledgedCount: a.acknowledgedCount != null ? a.acknowledgedCount : null,
    acknowledgedTotal: a.acknowledgedTotal != null ? a.acknowledgedTotal : null,
    attachmentList: a.attachmentList || [],
    resizedImageList: a.resizedImageList || [],
  }), []);

  // Header counts + truncation from the board endpoint (spec §4.1). Kept separate
  // from the paginated search list so the "N active · M urgent" summary is the
  // company's true active/urgent totals regardless of the current status filter.
  const loadBoardCounts = useCallback(() => {
    api.advisoryBoard(
      {},
      (result: any) => {
        if (result == null) return;
        if (result.totalActive != null) setTotalActive(result.totalActive);
        if (result.totalUrgent != null) setTotalUrgent(result.totalUrgent);
        setBoardTruncated(!!result.truncated);
        if (result.truncated) {
          const rows = Array.isArray(result.rows) ? result.rows.length : null;
          setBoardCap(rows);
        } else {
          setBoardCap(null);
        }
      },
      () => {
        // Header counts are best-effort; a failure here must not pop an error.
      }
    );
  }, []);

  const loadLatestAck = useCallback(() => {
    api.getAdvisoryLatestAck(
      (result: any) => {
        setLastReviewedAt(result && result.acknowledgedAt ? result.acknowledgedAt : null);
        setUnreviewedCount(result && result.unreviewedCount != null ? result.unreviewedCount : 0);
      },
      () => {
        // Best-effort like loadBoardCounts: a failure here must not pop an error
        // or reset counts — keep the prior reviewed timestamp / unreviewed badge.
      }
    );
  }, []);

  const loadTypes = useCallback(() => {
    api.listAdvisoryTypes(
      (result: any) => {
        const sorted = [...(result.advisoryTypes || [])].sort((a, b) => a.id - b.id);
        setAdvisoryTypes(sorted);
      },
      () => {
        App.showPopupNotification('Error getting advisory types.', 'error');
      }
    );
  }, []);

  const loadClientSites = useCallback((clientId: number | null) => {
    if (!clientId) {
      setClientSites([]);
      return;
    }
    api.listClientGroupSite(
      clientId,
      (result: any) => {
        setClientSites(result || []);
      },
      () => {
        setClientSites([]);
      }
    );
  }, []);

  // ===== Refresh =====

  const refreshPage = useCallback(() => {
    loadAdvisories();
    loadBoardCounts();
    loadLatestAck();
  }, [loadAdvisories, loadBoardCounts, loadLatestAck]);

  useEffect(() => {
    advisoryScreenRefreshCallback = refreshPage;
    return () => {
      advisoryScreenRefreshCallback = null;
    };
  }, [refreshPage]);

  useEffect(() => {
    resetAdvisoryScreenRefresh();
    return () => {
      clearAdvisoryScreenRefresh();
    };
  }, []);

  // Recompute recurring-card "in effect now / next window" state on a 1-min tick
  // (cheap, board-only re-render) so the live pill stays accurate between the
  // 5-min data poll. Data itself is unchanged; only the derived state updates.
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  // Realtime: reload immediately when the backend pushes an advisory change to
  // the company read-only group (bridged to 'orna:advisory-changed'). Also
  // refresh when the tab regains focus / becomes visible. All reloads here send
  // logView:false (firstLoadRef is already consumed by the mount load).
  useEffect(() => {
    const onAdvisoryChanged = () => {
      if (advisoryScreenRefreshCallback) advisoryScreenRefreshCallback();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && advisoryScreenRefreshCallback) {
        advisoryScreenRefreshCallback();
      }
    };
    window.addEventListener('orna:advisory-changed', onAdvisoryChanged);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('orna:advisory-changed', onAdvisoryChanged);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // ===== Initial load =====

  useEffect(() => {
    // Advisories support only two recipient types: All Clients and Single Client.
    // INTERNAL ('i') is reused from Notes but excluded here — advisories have no client-facing
    // surface, so INTERNAL would be indistinguishable from All Clients.
    setRecipientTypes((Codes.getNoteRecipientCodes() || []).filter((rt: any) => rt.code !== 'i'));
    loadTypes();
    api.listClientGroup((result: any) => {
      if (result && result.npsdClientList) {
        setClients(
          [...result.npsdClientList].sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
        );
      }
    });
    loadLatestAck();
    loadBoardCounts();

    // Deep-link prefilter handoff (CallCard / client / site "advisories for this
    // site"): pre-scope the board to a client (+ optional site) before the first
    // load, then clear the handoff so a later plain navigation isn't re-filtered.
    const prefilter = (window as any).__ornaAdvisoryPrefilter;
    if (prefilter && prefilter.clientId != null) {
      const next: AdvisoryFiltersState = {
        status: 'ALL',
        client: prefilter.clientId,
        npsdClientSiteId: prefilter.siteId != null ? prefilter.siteId : null,
        advisoryTypeId: null,
      };
      filtersRef.current = next;
      setFilters(next);
      loadFilterClientSites(prefilter.clientId);
      try {
        delete (window as any).__ornaAdvisoryPrefilter;
      } catch {
        (window as any).__ornaAdvisoryPrefilter = undefined;
      }
    }

    loadAdvisories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Filter handlers =====

  const toggleFilters = useCallback(() => setFiltersExpanded((p) => !p), []);

  const loadFilterClientSites = useCallback((clientId: number | null) => {
    if (!clientId) {
      setFilterClientSites([]);
      return;
    }
    api.listClientGroupSite(
      clientId,
      (result: any) => setFilterClientSites(result || []),
      () => setFilterClientSites([])
    );
  }, []);

  const clearFilters = useCallback(() => {
    const cleared: AdvisoryFiltersState = {
      status: defaultStatus,
      client: null,
      npsdClientSiteId: null,
      advisoryTypeId: null,
    };
    setFilters(cleared);
    filtersRef.current = cleared;
    setFilterClientSites([]);
    setCurrentPage(1);
    currentPageRef.current = 1;
    loadAdvisories(1);
  }, [loadAdvisories, defaultStatus]);

  const updateFilter = useCallback(
    (field: keyof AdvisoryFiltersState, value: string | number | null) => {
      // Apply filter changes immediately: build the next filters object
      // explicitly so the ref is synchronously up to date before loadAdvisories
      // reads filtersRef.current, then reset to page 1 and reload.
      const next = { ...filtersRef.current, [field]: value } as AdvisoryFiltersState;
      // Changing the client invalidates the site selection (sites belong to a
      // client); clear it and reload that client's site list.
      if (field === 'client') {
        next.npsdClientSiteId = null;
        loadFilterClientSites(value as number | null);
      }
      filtersRef.current = next;
      setFilters(next);
      setCurrentPage(1);
      currentPageRef.current = 1;
      loadAdvisories(1);
    },
    [loadAdvisories, loadFilterClientSites]
  );

  // ===== Pagination =====

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return;
      setCurrentPage(page);
      currentPageRef.current = page;
      loadAdvisories(page);
    },
    [totalPages, loadAdvisories]
  );

  // ===== Form modal =====

  const openAddModal = useCallback(() => {
    const fresh = createEmptyEditingAdvisory();
    const types = Codes.getNoteRecipientCodes() || [];
    if (types.length > 0) fresh.recipientType = types[0].code;
    setFormErrors({});
    setIsNewAdvisory(true);
    setIsReadOnlyView(false);
    setEditingAdvisory(fresh);
    setClientSites([]);
    setIsStartPickerOpen(false);
    setIsEndPickerOpen(false);
    setShowFormModal(true);
  }, []);

  const openEditModal = useCallback(
    (row: AdvisoryListItem, readOnly = false) => {
      const hasWindow = !!(row.startDateTime || row.endDateTime || row.recurring);
      const modified: EditingAdvisory = {
        id: row.id,
        displayId: row.displayId,
        advisoryTypeId: row.advisoryTypeId,
        recipientType: row.recipientType,
        npsdClient: row.npsdClient,
        selectedSiteIds: (row.selectedClientSites || []).map((s: any) => s.id),
        subject: row.subject,
        details: row.details,
        location: row.location,
        urgent: !!row.urgent,
        isTemporary: hasWindow,
        recurring: !!row.recurring,
        recurrenceDays: row.recurrenceDays || [],
        recurrenceStartTime: row.recurrenceStartTime || '',
        recurrenceEndTime: row.recurrenceEndTime || '',
        startDateTime: row.startDateTime ? new Date(row.startDateTime) : null,
        endDateTime: row.endDateTime ? new Date(row.endDateTime) : null,
        attachmentList: deepCopyProperties(row.attachmentList || []),
        resizedImageList: deepCopyProperties(row.resizedImageList || []),
        attachmentInfoList: getAttachmentInfoList(row.attachmentList || []),
      };
      setFormErrors({});
      setIsNewAdvisory(false);
      setIsReadOnlyView(readOnly);
      setEditingAdvisory(modified);
      if (row.npsdClient) {
        loadClientSites(row.npsdClient);
      } else {
        setClientSites([]);
      }
      setIsStartPickerOpen(false);
      setIsEndPickerOpen(false);
      setShowFormModal(true);
    },
    [loadClientSites]
  );

  const closeFormModal = useCallback(() => {
    setShowFormModal(false);
    setIsReadOnlyView(false);
    setIsStartPickerOpen(false);
    setIsEndPickerOpen(false);
    setFormErrors({});
    setEditingAdvisory(createEmptyEditingAdvisory());
  }, []);

  // ===== Form field handlers =====

  // Clear a single inline field error the moment the user changes that field, so
  // corrected fields stop showing an error. This does NOT re-validate on every
  // keystroke — it only removes an error that is already showing.
  const clearFieldError = useCallback((field: keyof AdvisoryFieldErrors) => {
    setFormErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const onAdvisoryTypeChange = useCallback((value: string | number | null) => {
    setEditingAdvisory((prev) => ({ ...prev, advisoryTypeId: value as number | null }));
    clearFieldError('advisoryTypeId');
  }, [clearFieldError]);

  const onRecipientTypeChange = useCallback((value: string | number | null) => {
    setEditingAdvisory((prev) => {
      const updated = { ...prev, recipientType: value as string };
      if (value !== 'c') {
        updated.npsdClient = null;
        updated.selectedSiteIds = [];
        setClientSites([]);
      }
      return updated;
    });
    clearFieldError('recipientType');
    // Switching away from a specific client makes any client error moot.
    if (value !== 'c') clearFieldError('npsdClient');
  }, [clearFieldError]);

  const onClientChange = useCallback(
    (value: string | number | null) => {
      const clientId = value as number | null;
      setEditingAdvisory((prev) => ({ ...prev, npsdClient: clientId, selectedSiteIds: [] }));
      if (clientId) {
        loadClientSites(clientId);
      } else {
        setClientSites([]);
      }
      clearFieldError('npsdClient');
    },
    [loadClientSites, clearFieldError]
  );

  const onSiteIdsChange = useCallback((values: any[]) => {
    setEditingAdvisory((prev) => ({ ...prev, selectedSiteIds: values }));
  }, []);

  const onToggleUrgent = useCallback((value: boolean) => {
    setEditingAdvisory((prev) => ({ ...prev, urgent: value }));
  }, []);

  const onToggleTemporary = useCallback((value: boolean) => {
    setEditingAdvisory((prev) => {
      if (!value) {
        return {
          ...prev,
          isTemporary: false,
          recurring: false,
          startDateTime: null,
          endDateTime: null,
          recurrenceDays: [],
          recurrenceStartTime: '',
          recurrenceEndTime: '',
        };
      }
      return { ...prev, isTemporary: true };
    });
  }, []);

  const onRecurringModeChange = useCallback((recurring: boolean) => {
    setEditingAdvisory((prev) => ({ ...prev, recurring }));
  }, []);

  const onToggleRecurrenceDay = useCallback((code: string) => {
    setEditingAdvisory((prev) => {
      const set = new Set(prev.recurrenceDays || []);
      if (set.has(code)) set.delete(code);
      else set.add(code);
      return { ...prev, recurrenceDays: Array.from(set) };
    });
    clearFieldError('recurrenceDays');
  }, [clearFieldError]);

  const onRecurrenceStartTimeChange = useCallback((value: string) => {
    setEditingAdvisory((prev) => ({ ...prev, recurrenceStartTime: value }));
    clearFieldError('recurrenceStartTime');
    // The "end must be after start" check lives on the end-time error; changing
    // the start can resolve it, so clear that too.
    clearFieldError('recurrenceEndTime');
  }, [clearFieldError]);

  const onRecurrenceEndTimeChange = useCallback((value: string) => {
    setEditingAdvisory((prev) => ({ ...prev, recurrenceEndTime: value }));
    clearFieldError('recurrenceEndTime');
  }, [clearFieldError]);

  const onStartDateTimeChange = useCallback((value: Date | null) => {
    setEditingAdvisory((prev) => ({ ...prev, startDateTime: value }));
    // The one-time "after start" check lives on the end-date error.
    clearFieldError('endDateTime');
  }, [clearFieldError]);

  const onEndDateTimeChange = useCallback((value: Date | null) => {
    setEditingAdvisory((prev) => ({ ...prev, endDateTime: value }));
    clearFieldError('endDateTime');
  }, [clearFieldError]);

  // Subject/Details/Location flow through this generic editing-change updater.
  // Clear the subject/details inline errors when their value actually changes
  // (location has no validation). Uses the synced ref for the prior value so we
  // avoid nesting one state update inside another.
  const onEditingAdvisoryChange = useCallback(
    (updater: (prev: EditingAdvisory) => EditingAdvisory) => {
      const prev = editingAdvisoryRef.current;
      const next = updater(prev);
      setEditingAdvisory(next);
      setFormErrors((errs) => {
        if (!errs.subject && !errs.details) return errs;
        const cleared = { ...errs };
        if (errs.subject && next.subject !== prev.subject) delete cleared.subject;
        if (errs.details && next.details !== prev.details) delete cleared.details;
        return cleared;
      });
    },
    []
  );

  const openStartPicker = useCallback(() => {
    setTimeout(() => setIsStartPickerOpen(true), 150);
  }, []);
  const closeStartPicker = useCallback(() => setIsStartPickerOpen(false), []);
  const openEndPicker = useCallback(() => {
    setTimeout(() => setIsEndPickerOpen(true), 150);
  }, []);
  const closeEndPicker = useCallback(() => setIsEndPickerOpen(false), []);

  // ===== Attachments =====

  const getResizedImageUrl = useCallback((attachmentId: number): string => {
    const resizedImage = getObjectInArrayByProperty(
      editingAdvisoryRef.current.resizedImageList,
      'id',
      attachmentId
    );
    return resizedImage ? resizedImage.signedURL : '';
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    const fileList = fileInput.files;
    if (!fileList || fileList.length === 0) return;
    const files: File[] = Array.from(fileList);
    fileInput.value = '';
    uploadAttachments(files);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadAttachments = useCallback((files: File[]) => {
    const attachmentList: any[] = [];
    for (const file of files) {
      const attachment: any = {
        id: null,
        signedURL: '',
        filename: file.name,
        title: '',
        typeName: null,
        createdBy: '',
        dateCreated: '',
      };
      const lastDotIndex = file.name.lastIndexOf('.');
      attachment.title = lastDotIndex > 0 ? file.name.substring(0, lastDotIndex) : file.name;
      attachmentList.push(attachment);
    }

    setIsUploadingAttachment(true);
    App.uploadStarted();
    App.uploadProgress(1);
    App.initBlobUploadProgressControl();

    api.saveAttachment(attachmentList, (result: any) => {
      App.uploadProgress(1);
      for (let i = 0; i < result.azureStorageBlobInfoList.length; i++) {
        load.showLoadingScreen();
        App.blobUploadStarted();

        const file = files[i];
        const attachmentInfo: any = { id: null, activationKey: '' };
        const blobInfo = result.azureStorageBlobInfoList[i];

        attachmentInfo.id = blobInfo.attachmentId;
        attachmentInfo.activationKey = blobInfo.activationKey;

        const sasReadToken: string = blobInfo.sasReadToken;
        const sasWriteToken: string = blobInfo.sasWriteToken;
        const host: string = blobInfo.host;
        const containerName: string = blobInfo.containerName;
        const blobName: string = blobInfo.blobName;
        const attachment = attachmentList[i];

        attachment.id = blobInfo.attachmentId;
        attachment.signedURL = `${host}/${containerName}/${blobName}?${sasReadToken}`;

        setEditingAdvisory((prev) => ({
          ...prev,
          attachmentInfoList: [...prev.attachmentInfoList, attachmentInfo],
          attachmentList: [...prev.attachmentList, attachment],
        }));

        const customBlockSize =
          file.size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 512;

        uploadBlockBlob(host, sasWriteToken, containerName, blobName, file, {
          blockSize: customBlockSize,
          onProgress: (percent) => {
            App.setBlobUploadProgress(percent);
            App.uploadProgress(percent);
          },
        })
          .then(() => {
            App.uploadCompleted();
            App.showPopupNotification('File uploaded.', 'success');
            // Only image uploads get a resized preview; pushing non-images here
            // would make getResizedImageUrl return a URL and render a broken
            // <img> for PDFs/docs. Non-images stay in attachmentList only.
            if (file.type && file.type.startsWith('image/')) {
              setEditingAdvisory((prev) => ({
                ...prev,
                resizedImageList: [...prev.resizedImageList, attachment],
              }));
            }
            App.blobUploadCompleted();
            load.hideLoadingScreen();
            setIsUploadingAttachment(false);
          })
          .catch(() => {
            App.uploadCompleted();
            App.showPopupNotification('File failed to upload.', 'error');
            App.blobUploadCompleted();
            load.hideLoadingScreen();
            setIsUploadingAttachment(false);
          });
      }
    });
  }, []);

  const removeAttachment = useCallback((attachmentId: number) => {
    setEditingAdvisory((prev) => ({
      ...prev,
      resizedImageList: prev.resizedImageList.filter((item: any) => item.id !== attachmentId),
      attachmentList: prev.attachmentList.filter((item: any) => item.id !== attachmentId),
      attachmentInfoList: prev.attachmentInfoList.filter((item: any) => item.id !== attachmentId),
    }));
  }, []);

  // ===== Save =====

  const buildCommand = useCallback((adv: EditingAdvisory) => {
    let client: { id: number; sites: number[] } | null = null;
    if (adv.recipientType === 'c' && adv.npsdClient) {
      client = { id: adv.npsdClient, sites: adv.selectedSiteIds || [] };
    }

    const startIso =
      adv.isTemporary && adv.startDateTime
        ? moment(adv.startDateTime).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000Z'
        : null;
    const endIso =
      adv.isTemporary && adv.endDateTime
        ? moment(adv.endDateTime).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000Z'
        : null;

    // Recurrence fields only when the temporary window is Recurring (spec §2.1);
    // the backend requires all four absent otherwise. For recurring advisories,
    // start/end serve as the OUTER date range (spec §1.1).
    const isRecurring = adv.isTemporary && adv.recurring;

    return {
      advisoryTypeId: adv.advisoryTypeId,
      recipientType: adv.recipientType,
      client: client,
      subject: adv.subject,
      details: adv.details,
      location: adv.location || '',
      urgent: !!adv.urgent,
      recurring: isRecurring,
      recurrenceDays: isRecurring ? adv.recurrenceDays || [] : null,
      recurrenceStartTime: isRecurring ? adv.recurrenceStartTime || null : null,
      recurrenceEndTime: isRecurring ? adv.recurrenceEndTime || null : null,
      recurrenceZoneId: isRecurring ? browserZoneId() : null,
      startDateTime: startIso,
      endDateTime: endIso,
      attachmentInfoList: adv.attachmentInfoList || [],
    };
  }, []);

  const saveAdvisory = useCallback(() => {
    const adv = editingAdvisoryRef.current;

    // Inline field validation (spec §2.2 mirrored client-side). All problems are
    // shown at once on the fields they belong to instead of one popup at a time.
    const errors = validateAdvisory(adv, { isNew: !adv.id });
    if (hasErrors(errors)) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    setIsSaving(true);
    const command = buildCommand(adv);

    if (adv.id) {
      api.updateAdvisory(
        adv.id,
        command,
        () => {
          setIsSaving(false);
          App.showPopupNotification('Advisory updated successfully.', 'success');
          closeFormModal();
          refreshPage();
        },
        () => {
          // Reset the busy flag on failure so the Save button isn't stuck on
          // "Saving..."; the server-error display is handled by ajaxCall.
          setIsSaving(false);
        }
      );
    } else {
      api.saveAdvisory(
        command,
        () => {
          setIsSaving(false);
          App.showPopupNotification('Advisory saved successfully.', 'success');
          closeFormModal();
          refreshPage();
        },
        () => {
          setIsSaving(false);
        }
      );
    }
  }, [buildCommand, closeFormModal, refreshPage]);

  // ===== Row actions =====

  // Restore is only for soft-DELETED advisories (undo the delete). Expired
  // advisories are brought back via Edit, so the user consciously sets a new
  // window (extend) or turns off the window to make it permanent — rather than
  // silently making it permanent here.
  const handleRestore = useCallback(
    (row: AdvisoryListItem) => {
      setRestoringId(row.id);
      api.restoreAdvisory(
        row.id,
        () => {
          setRestoringId(null);
          App.showPopupNotification('Advisory restored.', 'success');
          refreshPage();
        },
        () => {
          // Reset the busy flag on failure so the Restore button isn't stuck
          // disabled; the server-error display is handled by ajaxCall.
          setRestoringId(null);
          App.showPopupNotification('Failed to restore advisory.', 'error');
        }
      );
    },
    [refreshPage]
  );

  const confirmDelete = useCallback((row: AdvisoryListItem) => {
    setAdvisoryToDelete(row);
    setShowDeleteModal(true);
  }, []);

  const deleteAdvisory = useCallback(() => {
    if (!advisoryToDelete) return;
    setIsDeleting(true);
    api.deleteAdvisory(
      advisoryToDelete.id,
      () => {
        setIsDeleting(false);
        App.showPopupNotification('Advisory deleted.', 'success');
        setShowDeleteModal(false);
        setAdvisoryToDelete(null);
        refreshPage();
      },
      () => {
        // Reset the busy flag on failure so the Delete button isn't stuck;
        // the server-error display is handled by ajaxCall.
        setIsDeleting(false);
      }
    );
  }, [advisoryToDelete, refreshPage]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setAdvisoryToDelete(null);
  }, []);

  // ===== Mark reviewed (board-level acknowledgement) =====

  const markReviewed = useCallback(() => {
    setIsAcknowledging(true);
    api.acknowledgeAdvisories(
      (result: any) => {
        setIsAcknowledging(false);
        if (result && result.acknowledgedAt) {
          setLastReviewedAt(result.acknowledgedAt);
        }
        // A truthful "you're caught up": the review badge clears immediately, and
        // the freshness badges recompute against the new review timestamp.
        setUnreviewedCount(0);
        App.showPopupNotification('Marked reviewed.', 'success');
      },
      () => {
        // Reset the busy flag on failure so "Mark reviewed" isn't stuck disabled;
        // the server-error display is handled by ajaxCall.
        setIsAcknowledging(false);
      }
    );
  }, []);

  // ===== Per-advisory acknowledgment (urgent cards) =====

  const acknowledgeCard = useCallback(
    (row: AdvisoryListItem) => {
      setAckingId(row.id);
      api.acknowledgeAdvisory(
        row.id,
        (result: any) => {
          setAckingId(null);
          // Optimistically reflect the acknowledgment on the card, then reload so
          // the "X of Y" progress count reflects everyone.
          setAdvisories((prev) =>
            prev.map((r) =>
              r.id === row.id
                ? {
                    ...r,
                    currentUserAcknowledgment: {
                      acknowledgedAt: result && result.acknowledgedAt ? result.acknowledgedAt : new Date().toISOString(),
                      stale: false,
                    },
                    acknowledgedCount:
                      r.currentUserAcknowledgment && !r.currentUserAcknowledgment.stale
                        ? r.acknowledgedCount
                        : (r.acknowledgedCount || 0) + 1,
                  }
                : r
            )
          );
          App.showPopupNotification('Acknowledged.', 'success');
        },
        () => {
          setAckingId(null);
          App.showPopupNotification('Failed to acknowledge.', 'error');
        }
      );
    },
    []
  );

  const openAckNames = useCallback((row: AdvisoryListItem) => {
    setAckNamesSubject(row.subject);
    setAckNames([]);
    setAckNamesError(false);
    setAckNamesLoading(true);
    setAckNamesOpen(true);
    api.getAdvisoryAcknowledgments(
      row.id,
      (result: any) => {
        setAckNames(Array.isArray(result) ? result : result && result.acknowledgments ? result.acknowledgments : []);
        setAckNamesError(false);
        setAckNamesLoading(false);
      },
      () => {
        setAckNames([]);
        setAckNamesError(true);
        setAckNamesLoading(false);
      }
    );
  }, []);

  const closeAckNames = useCallback(() => {
    setAckNamesOpen(false);
    setAckNames([]);
  }, []);

  // ===== Offline last-known board =====

  const showLastKnown = useCallback(() => {
    try {
      const raw = localStorage.getItem(offlineCacheKey());
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.rows)) {
        setAdvisories(sortUrgentFirst(parsed.rows));
        setOfflineAt(parsed.at || null);
        setShowOffline(true);
        setLoadError(false);
      }
    } catch {
      /* corrupt cache — ignore */
    }
  }, []);

  const lastReviewedLabel = useMemo(() => {
    if (!lastReviewedAt) return '';
    const m = moment(lastReviewedAt);
    return m.isSame(moment(), 'day')
      ? `Reviewed at ${formatAckTimestamp(lastReviewedAt)}`
      : `Last reviewed ${formatAckTimestamp(lastReviewedAt)}`;
  }, [lastReviewedAt]);

  // Offline banner timestamp ("as of 6:02 AM").
  const offlineLabel = useMemo(() => {
    if (!offlineAt) return '';
    return moment(offlineAt).format('h:mm A');
  }, [offlineAt]);

  // ===== Render =====

  const count = advisories.length;

  return (
    <div className={`advisory-board-screen ${theme}`.trim()}>
      <AdvisoryFormModal
        open={showFormModal}
        isNewAdvisory={isNewAdvisory}
        readOnly={isReadOnlyView}
        isSaving={isSaving}
        isUploadingAttachment={isUploadingAttachment}
        editingAdvisory={editingAdvisory}
        advisoryTypes={advisoryTypes}
        recipientTypeItems={recipientTypeItems}
        clientItems={clientItems}
        clientSites={clientSites}
        isStartPickerOpen={isStartPickerOpen}
        isEndPickerOpen={isEndPickerOpen}
        theme={theme}
        errors={formErrors}
        onSave={saveAdvisory}
        onCancel={closeFormModal}
        onAdvisoryTypeChange={onAdvisoryTypeChange}
        onRecipientTypeChange={onRecipientTypeChange}
        onClientChange={onClientChange}
        onSiteIdsChange={onSiteIdsChange}
        onToggleUrgent={onToggleUrgent}
        onToggleTemporary={onToggleTemporary}
        onRecurringModeChange={onRecurringModeChange}
        onToggleRecurrenceDay={onToggleRecurrenceDay}
        onRecurrenceStartTimeChange={onRecurrenceStartTimeChange}
        onRecurrenceEndTimeChange={onRecurrenceEndTimeChange}
        onStartDateTimeChange={onStartDateTimeChange}
        onEndDateTimeChange={onEndDateTimeChange}
        onOpenStartPicker={openStartPicker}
        onCloseStartPicker={closeStartPicker}
        onStartPickerOpenChange={setIsStartPickerOpen}
        onOpenEndPicker={openEndPicker}
        onCloseEndPicker={closeEndPicker}
        onEndPickerOpenChange={setIsEndPickerOpen}
        onEditingAdvisoryChange={onEditingAdvisoryChange}
        onFileSelect={handleFileSelect}
        onRemoveAttachment={removeAttachment}
        getResizedImageUrl={getResizedImageUrl}
      />

      <ConfirmModal
        open={showDeleteModal}
        title="Delete Advisory"
        messageHtml={deleteConfirmMessage}
        confirmText="Delete"
        cancelText="Cancel"
        confirmClass="danger"
        confirmDisabled={isDeleting}
        busy={isDeleting}
        theme={theme}
        onConfirm={deleteAdvisory}
        onCancel={cancelDelete}
      />

      <AdvisoryAckNamesModal
        open={ackNamesOpen}
        loading={ackNamesLoading}
        error={ackNamesError}
        subject={ackNamesSubject}
        names={ackNames}
        theme={theme}
        onClose={closeAckNames}
      />

      {/* Header bar */}
      <div className="header">
        <h1>
          Advisories
          {/* Nav badge surrogate: the CAD/MDT nav menu is legacy (non-React), so
              the "new/updated since last review" count is surfaced here on the
              screen header. Clears on Mark reviewed / recomputes on realtime. */}
          {unreviewedCount > 0 && (
            <span className="advisory-unreviewed-badge" title="New or updated since your last review">
              {unreviewedCount}
            </span>
          )}
        </h1>
        <div className="header-actions">
          {/* Rendered only once the board summary has answered — never assert
              "0 active" about a board we haven't seen yet (initial load, load
              error). totalRecords is NOT a fallback: it counts the filtered
              search result, not actives. */}
          {totalActive != null && (
            <span className="advisory-counts">
              <strong>{totalActive} active</strong>
              {totalUrgent != null && totalUrgent > 0 && (
                <span className="advisory-counts__urgent"> · {totalUrgent} urgent</span>
              )}
            </span>
          )}
          {lastReviewedLabel && (
            <span className="header-reviewed-note">{lastReviewedLabel}</span>
          )}
          <button
            type="button"
            className="btn-reviewed"
            onClick={markReviewed}
            disabled={isAcknowledging}
            title="Mark these advisories as reviewed"
          >
            <i className="fa fa-check"></i> Mark Reviewed
          </button>
          {canCreate && (
            <button className="btn-new" onClick={openAddModal}>
              <i className="fa fa-plus"></i> New Advisory
            </button>
          )}
        </div>
      </div>

      {/* Filter bar (available to everyone) */}
      <AdvisoryFilters
        filters={filters}
        filtersExpanded={filtersExpanded}
        activeFilterCount={activeFilterCount}
        clientItems={clientItems}
        siteItems={filterSiteItems}
        typeItems={typeFilterItems}
        onToggleFilters={toggleFilters}
        onClear={clearFilters}
        onUpdateFilter={updateFilter}
      />

      {/* Offline last-known banner (rendered above the cached cards) */}
      {showOffline && (
        <div className="advisory-offline-banner">
          <i className="fa fa-exclamation-triangle"></i>
          <span>
            Offline — showing advisories as of <strong>{offlineLabel}</strong>. Retry to refresh.
          </span>
          <button type="button" className="advisory-offline-retry" onClick={() => loadAdvisories()}>
            <i className="fa fa-refresh"></i> Retry
          </button>
        </div>
      )}

      {/* Cards */}
      <div className="advisory-board-cards">
        {count === 0 && isLoading && !loadError && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
          </div>
        )}

        {/* Distinct load-error state — never the empty state on failure. */}
        {loadError && !showOffline && (
          <div className="advisory-error-state">
            <div className="advisory-error-state__icon">
              <i className="fa fa-plug"></i>
            </div>
            <h3>Couldn't load advisories</h3>
            <p>Something went wrong loading the board. Some advisories may not be shown. Retry to refresh.</p>
            <div className="advisory-error-state__actions">
              <button type="button" className="btn-new" onClick={() => loadAdvisories()}>
                <i className="fa fa-refresh"></i> Retry
              </button>
              {offlineAt && (
                <button type="button" className="btn-clear" onClick={showLastKnown}>
                  Show Last Known ({offlineLabel})
                </button>
              )}
            </div>
          </div>
        )}

        {count === 0 && !isLoading && !loadError && (
          <div className="empty-state">
            <div className="empty-state-icon">
              <i className="fa fa-bullhorn"></i>
            </div>
            <div className="empty-state-title">No advisories</div>
            <div>Nothing matches the current filters.</div>
          </div>
        )}

        {(!loadError || showOffline) && advisories.map((row) => {
          const att = attachmentLabel(row);
          const isDeleted = row.status === 'DELETED';
          const isExpired = row.status === 'EXPIRED';
          const fresh = freshnessFor(row, lastReviewedAt);
          const ack = row.currentUserAcknowledgment;
          const showAckProgress =
            row.urgent && row.acknowledgedTotal != null && row.acknowledgedTotal > 0;

          // Recurring rows always show their schedule + a live in-effect pill.
          // Times on the schedule line are the advisory's wall-clock (with a short
          // zone label only when it differs from the viewer's); the pill's live
          // times are in the viewer's local zone.
          const now = new Date(nowTick);
          const rec = row.recurring
            ? computeRecurrenceState(row, now)
            : null;
          let recSchedule = '';
          let recPillKind: 'in' | 'out' | 'none' = 'none';
          let recPillText = '';
          if (row.recurring) {
            const recStartMin = hhmmToMinutes(row.recurrenceStartTime);
            const recEndMin = hhmmToMinutes(row.recurrenceEndTime);
            const recOvernight =
              recStartMin != null && recEndMin != null && recEndMin < recStartMin;
            const daysLabel = formatRecurrenceDays(row.recurrenceDays, recOvernight);
            const startLbl = formatHhmmForDisplay(row.recurrenceStartTime);
            const endLbl = formatHhmmForDisplay(row.recurrenceEndTime);
            const zoneLbl =
              row.recurrenceZoneId && row.recurrenceZoneId !== viewerZone
                ? zoneShortName(row.recurrenceZoneId, now)
                : '';
            recSchedule =
              `${daysLabel} ${startLbl} – ${endLbl}` +
              (zoneLbl ? ` (${zoneLbl})` : '') +
              (row.endDateTime
                ? ` · through ${moment(row.endDateTime).format('MMM D, YYYY')}`
                : '');
            if (rec && rec.inSlotNow && rec.currentSlotEnd) {
              recPillKind = 'in';
              recPillText = `In effect now · until ${moment(rec.currentSlotEnd).format('h:mm A')}`;
            } else if (rec && rec.nextWindowStart) {
              recPillKind = 'out';
              recPillText = `Outside scheduled hours · next ${formatNextWindow(rec.nextWindowStart, now)}`;
            } else {
              recPillKind = 'none';
              recPillText = 'No remaining windows';
            }
          }
          // Slot-aware state: an ACTIVE recurring row outside its daily slot
          // answers "does this apply right now?" directly — a "Not in effect"
          // badge (amber, matching the pill) plus a muted card background —
          // instead of a green "Active" that would contradict the schedule line.
          const outOfSlot =
            row.recurring && row.status === 'ACTIVE' && rec != null && !rec.inSlotNow;
          return (
            <div
              key={row.id}
              className={`advisory-board-card${row.urgent ? ' is-urgent' : ''}${isDeleted ? ' is-deleted' : ''}${outOfSlot ? ' is-off-hours' : ''}`}
              style={{ borderLeft: '4px solid #' + (row.borderColor || FALLBACK_BORDER) }}
              title="View advisory"
              onClick={() => openEditModal(row, true)}
            >
              {/* URGENT band — theme-safe, independent of the type color. */}
              {row.urgent && (
                <div className="advisory-urgent-band">
                  <i className="fa fa-exclamation-triangle"></i> URGENT
                  <span className="advisory-urgent-band__status">
                    {ack && ack.stale
                      ? 'updated since your acknowledgment'
                      : ack
                        ? 'acknowledged'
                        : 'requires acknowledgment'}
                  </span>
                </div>
              )}

              <div className="advisory-card-body">
                {/* Chip + subject + freshness + status badge + actions */}
                <div className="advisory-card-head">
                  <span className="advisory-type-chip" style={chipColors(row)}>
                    {row.typeName || '—'}
                  </span>
                  <button
                    type="button"
                    className={`advisory-card-subject${isDeleted ? ' is-struck' : ''}`}
                    aria-label={`View advisory: ${row.subject}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(row, true);
                    }}
                  >
                    {row.subject}
                  </button>
                  {fresh === 'new' && (
                    <span className="advisory-fresh advisory-fresh--new">
                      <span className="advisory-fresh__dot"></span> NEW
                    </span>
                  )}
                  {fresh === 'updated' && (
                    <span className="advisory-fresh advisory-fresh--updated">
                      <span className="advisory-fresh__dot"></span> UPDATED
                    </span>
                  )}
                  {isExpired && (
                    <span className="advisory-status advisory-status--expired">
                      <i className="fa fa-clock"></i> Expired
                    </span>
                  )}
                  {isDeleted && (
                    <span className="advisory-status advisory-status--deleted">
                      <i className="fa fa-trash"></i> Deleted
                    </span>
                  )}
                  {!isExpired && !isDeleted && (
                    outOfSlot ? (
                      <span className="advisory-status advisory-status--out">
                        Not in effect
                      </span>
                    ) : (
                      <span className={`advisory-status advisory-status--${row.status.toLowerCase()}`}>
                        {statusLabel(row.status)}
                      </span>
                    )
                  )}

                  {/* Edit affordances (right-aligned). Deleted → Restore/View only. */}
                  {canEditAny && (
                    <div className="advisory-card-actions">
                      {isDeleted
                        ? canModify && (
                            <button
                              type="button"
                              className="btn-action btn-modify"
                              title="Restore"
                              aria-label="Restore"
                              disabled={restoringId === row.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRestore(row);
                              }}
                            >
                              <i className="fa fa-undo"></i>
                            </button>
                          )
                        : canModify && (
                            <button
                              type="button"
                              className="btn-action btn-modify"
                              title={isExpired ? 'Edit / extend' : 'Edit'}
                              aria-label={isExpired ? 'Edit / extend' : 'Edit'}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(row);
                              }}
                            >
                              <i className="fa fa-pencil"></i>
                            </button>
                          )}
                      {canDelete && !isDeleted && (
                        <button
                          type="button"
                          className="btn-action btn-delete"
                          title="Delete"
                          aria-label="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(row);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Meta line */}
                <div className="advisory-card-meta">
                  <span title={sitesTitle(row)}>
                    <i className="fa fa-users"></i> {metaLabel(row)}
                  </span>
                  {row.location && (
                    <span>
                      <i className="fa fa-map-marker"></i> {row.location}
                    </span>
                  )}
                  {/* Recurring rows carry their schedule + through-date in the
                      dedicated recurrence line below, so the generic window label is
                      suppressed here. A time-bounded (non-recurring) advisory shows its
                      window; a standing advisory with no window shows a quiet "Ongoing"
                      marker — it's the calm default, so it stays recessive next to the
                      dated/recurring states that actually need an officer's attention. */}
                  {!row.recurring && (row.startDateTime || row.endDateTime) && (
                    <span>
                      <i className="fa fa-clock"></i> {windowLabel(row)}
                    </span>
                  )}
                  {!row.recurring && !row.startDateTime && !row.endDateTime && (
                    <span className="advisory-window-ongoing">Ongoing</span>
                  )}
                </div>

                {/* Recurring schedule + live in-effect pill (always shown for
                    recurring rows so officers can see whether it's active now). */}
                {row.recurring && (
                  <div className="advisory-recurrence">
                    <span className="advisory-recurrence__schedule">
                      <i className="fa fa-calendar"></i> {recSchedule}
                    </span>
                    <span className={`advisory-recurrence__pill advisory-recurrence__pill--${recPillKind}`}>
                      {recPillText}
                    </span>
                  </div>
                )}

                {/* Details */}
                {row.details && <div className="advisory-card-details">{row.details}</div>}

                {/* Attachment line */}
                {att && (
                  <div className="advisory-card-attachment">
                    <i className="fa fa-paperclip"></i> {att}
                  </div>
                )}

                {/* Urgent acknowledgment row (active urgent cards only) */}
                {row.urgent && !isDeleted && !isExpired && (
                  <div className="advisory-ack-row" onClick={(e) => e.stopPropagation()}>
                    {ack && !ack.stale ? (
                      <span className="advisory-acked">
                        <i className="fa fa-check"></i> Acknowledged {formatAckTimestamp(ack.acknowledgedAt)}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="advisory-ack-btn"
                        disabled={ackingId === row.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          acknowledgeCard(row);
                        }}
                      >
                        {ack && ack.stale ? 'Acknowledge Update' : 'Acknowledge'}
                      </button>
                    )}
                    {ack && ack.stale && (
                      <span className="advisory-ack-note">
                        was acknowledged {formatAckTimestamp(ack.acknowledgedAt)}
                      </span>
                    )}
                    {showAckProgress && (
                      <span className="advisory-ack-count">
                        <strong>{row.acknowledgedCount || 0} of {row.acknowledgedTotal}</strong> acknowledged
                        {canModify && (
                          <>
                            {' · '}
                            <button
                              type="button"
                              className="advisory-ack-viewwho"
                              onClick={(e) => {
                                e.stopPropagation();
                                openAckNames(row);
                              }}
                            >
                              View Acknowledgments
                            </button>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div className="advisory-card-foot">
                  <span>{footLogged(row)}</span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Truncation line — a capped list must never look complete. */}
        {boardTruncated && !showOffline && !loadError && (
          <div className="advisory-cap-line">
            Showing {boardCap != null ? boardCap : '200'} of {totalActive != null ? totalActive : '—'} — refine filters to see the rest
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && !showOffline && !loadError && (
          <div className="pagination-container">
            <button
              type="button"
              className="pagination-btn"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              <i className="fa fa-chevron-left"></i> Prev
            </button>
            <span className="pagination-info">
              Page {currentPage} of {totalPages} ({totalRecords} total)
            </span>
            <button
              type="button"
              className="pagination-btn"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next <i className="fa fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdvisoryScreen;
